import { ApiRequest } from '../baseService'

const BaseRoute = 'app/paperwork-review'

/**
 * Queue a Pre-Review. Returns immediately with a job id — the work runs in the
 * background because it reads several PDFs and makes several model calls.
 *
 * When the uploaded files have not changed since the last run, the previous run
 * is returned instead of a new one being started, so re-opening the screen costs
 * nothing. Pass `force` to re-run anyway.
 */
const Start = (fromModule: string, objectId: string, force = false) => {
  return ApiRequest.post(
    `${BaseRoute}/start?fromModule=${fromModule}&objectId=${objectId}&force=${force}`
  )
}

/** Where a run has got to. Light enough to poll every few seconds. */
const GetStatus = (jobId: string) => {
  return ApiRequest.get(`${BaseRoute}/${jobId}/status`)
}

/** The latest report for a case. */
const GetLatest = (fromModule: string, objectId: string) => {
  return ApiRequest.get(
    `${BaseRoute}/latest?fromModule=${fromModule}&objectId=${objectId}`
  )
}

/**
 * Record a person's decision about one checklist line. The automated verdict is
 * kept alongside it rather than overwritten.
 */
const Adjudicate = (matchId: string, verdict: string, note?: string) => {
  return ApiRequest.post(`${BaseRoute}/matches/${matchId}/adjudicate`, {
    verdict,
    note,
  })
}

/**
 * The page a document was identified from, as an object URL the browser can
 * show. Lets a reviewer check a finding against what the review actually saw —
 * for a scanned file the page image is the only thing it ever had.
 */
const GetPreviewUrl = async (documentId: string) => {
  const { data } = await ApiRequest.get(
    `${BaseRoute}/documents/${documentId}/preview`,
    { responseType: 'blob' }
  )

  return URL.createObjectURL(data)
}

/**
 * Records that a person has been through the whole bundle themselves.
 *
 * This is what turns everyday use into an evaluation set: on a confirmed run,
 * every line nobody corrected counts as one they agreed with — the only source
 * of labels for the answers the review was already sure of.
 */
const Confirm = (jobId: string) => {
  return ApiRequest.post(`${BaseRoute}/${jobId}/confirm`)
}

/**
 * Merge every Other Document in the pre-check into one PDF and file it in the
 * case's official Paperwork section. The server refuses when Paperwork already
 * holds Other Documents — the person deletes them first, deliberately.
 */
const MergeToPaperwork = (fromModule: string, objectId: string) => {
  return ApiRequest.post(
    `${BaseRoute}/merge-to-paperwork?fromModule=${fromModule}&objectId=${objectId}`
  )
}

/**
 * Copy one Paperwork attachment into the pre-check: the attached LOI into
 * PREREVIEW_LOI, an Other Document into PREREVIEW_OTHER_DOC. A real copy — the
 * two sides stay independently deletable.
 */
const CopyToPreCheck = (attachmentId: string) => {
  return ApiRequest.post(
    `${BaseRoute}/copy-to-pre-check?attachmentId=${attachmentId}`
  )
}

const API = {
  Start,
  MergeToPaperwork,
  CopyToPreCheck,
  GetStatus,
  GetLatest,
  GetPreviewUrl,
  Confirm,
  Adjudicate,
}

export default API
